import { type BackgroundColorValue, Div, Flex, Span, Text, useColorSchemeValue } from "@hopper-ui/components";
import { NotebookIcon } from "@hopper-ui/icons";
import { CustomButton, NotificationMutedIcon, Routes, useSharedTranslation } from "@workleap-ai/shared";
import type { SmartReport } from "@workleap-ai/shared/types";
import { useMemo } from "react";
import { useNavigate } from "react-router";

interface Props {
    report: SmartReport;
}

export function SmartReportsListItem({ report }: Props) {
    const { tShared } = useSharedTranslation();
    const navigate = useNavigate();
    const { cadence, isMuted } = report;

    const cadenceLabel = useMemo(() => {
        switch (cadence) {
            case "BiWeekly":
                return tShared("smartReports.cadence.biWeekly");
            case "Monthly":
                return tShared("smartReports.cadence.monthly");
            default:
                return cadence;
        }
    }, [cadence, tShared]);

    const onItemPress = () => {
        navigate(Routes.SmartReportsEdit());
    };

    const backgroundColorHover = useColorSchemeValue("core_orchid-bloom-25", "neutral-weak") as BackgroundColorValue;

    return (
        <Div
            position="relative"
            borderRadius="rounded-md"
            width="100%"
            UNSAFE_maxWidth="var(--wai-page-max-width)"
            marginX="auto"
        >
            <CustomButton
                border="none"
                backgroundColor="transparent"
                backgroundColorHover={backgroundColorHover}
                backgroundColorFocus="core_orchid-bloom-25"
                textAlign="left"
                width="100%"
                height="100%"
                borderRadius="rounded-md"
                className="wai-smart-reports-list-button"
                aria-label={tShared("smartReports.openReport")}
                onPress={onItemPress}
            >
                <Flex
                    className="wai-smart-reports-list-content"
                    paddingY="inset-sm"
                    paddingX="inset-md"
                    alignItems="center"
                    borderRadius="rounded-md"
                    gap="inline-md"
                >
                    <Div UNSAFE_marginLeft="-0.125rem">
                        <NotebookIcon fill="neutral-weak" />
                    </Div>
                    <Div flexGrow={1}>
                        <Flex alignItems="center" gap="inline-sm" wrap="wrap">
                            <Text fontFamily="body-sm-medium" fontWeight="body-sm-medium" fontSize="body-sm-medium" color="neutral">
                                {tShared("smartReports.engagementReportName")}
                            </Text>
                            {isMuted && (
                                <Flex gap="inline-xs" alignItems="center">
                                    <NotificationMutedIcon width={16} fill="var(--hop-neutral-icon-weak)" />
                                    <Text fontFamily="body-xs" fontWeight="body-xs" fontSize="body-xs" color="neutral-weak">
                                        {tShared("smartReports.unsubscribed")}
                                    </Text>
                                </Flex>
                            )}
                        </Flex>
                        <Div paddingTop="inset-xs">
                            <Text fontFamily="body-xs" fontWeight="body-xs" fontSize="body-xs" color="neutral-weak">
                                {tShared("smartReports.reportSent")} <Span fontWeight="heading-sm" color="neutral-weak" textTransform="lowercase">{cadenceLabel}</Span>
                            </Text>
                        </Div>
                    </Div>
                </Flex>
            </CustomButton>
        </Div>
    );
}
